/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 에디터의 복사(copy)/붙여넣기(paste) 이벤트를 처리한다.
 *
 * @file /src/rootin/events/CopyPaste.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 7.
 */
import { Editor } from '@rootin/core/Editor';
import { Block } from '@rootin/base/Block';
import { Element } from '@rootin/base/Element';
import { Container } from '@rootin/elements/container/Container';
import { Color } from '@rootin/tools/color/Color';
import { Html } from '@utils/Html';
import { Dom } from '@utils/Dom';

/**
 * 붙여넣기 전, 클립보드 HTML 을 정리할 때 그대로 남길 태그 목록
 */
const PASTE_TAGS = [
    'H1',
    'H2',
    'H3',
    'H4',
    'H5',
    'H6',
    'B',
    'U',
    'I',
    'SPAN',
    'A',
    'LABEL',
    'DEL',
    'ROOTIN-BLOCK',
    'ROOTIN-ELEMENT',
    'TABLE',
    'THEAD',
    'TBODY',
    'TFOOT',
    'TR',
    'TD',
    'TH',
    'CAPTION',
    'COLGROUP',
    'COL',
    'IMG',
    'UL',
    'OL',
    'LI',
];

/**
 * 위 태그 중 남겨둔 태그에서 그대로 유지할 속성
 */
const PASTE_ATTRS = [
    'style',
    'data-type',
    'data-role',
    'data-side',
    'data-color',
    'data-mode',
    'data-ratio',
    'data-attachment-id',
    'data-alt',
    'href',
    'target',
    'src',
];

export class CopyPaste {
    /**
     * 에디터의 복사(copy)/붙여넣기(paste) 이벤트를 처리한다.
     *
     * @param editor - 이벤트를 정의할 에디터
     */
    constructor(public editor: Editor) {}

    /**
     * 이벤트 리스너를 바인딩한다.
     *
     * @param $container - 이벤트를 받을 컨테이너 DOM
     */
    listen($container: Dom): void {
        // 블록 전체가 선택된 상태의 복사를 처리한다.
        $container.on('copy', (_$container, e) => {
            this.onCopy(e);
        });

        $container.on('paste', (_$container, e) => {
            this.onPaste(e);
        });
    }

    /**
     * 블록 전체가 선택된 상태의 복사를 처리한다.
     *
     * @param e - copy 이벤트
     */
    onCopy(e: ClipboardEvent): void {
        if (e.clipboardData === null) {
            return;
        }

        const selectedBlocks = this.editor.selection.getSelectedBlocks();
        if (selectedBlocks.length === 0) {
            this.#copyFocusedAnchor(e);
            return;
        }

        const range = this.editor.selection.getRange();
        if (range === null) {
            return;
        }

        const fragment = range.cloneContents();
        for (const block of selectedBlocks) {
            fragment.querySelectorAll(`[data-element^="${block.getId()}-"]`).forEach(($el) => $el.remove());

            const full = block.$getBlock().getEl().cloneNode(true) as HTMLElement;
            const partial = fragment.querySelector(`[data-block="${block.getId()}"]`);
            if (partial !== null) {
                partial.replaceWith(full);
            } else {
                fragment.appendChild(full);
            }
        }

        const $container = document.createElement('div');
        $container.appendChild(fragment);

        e.clipboardData.setData('text/html', $container.innerHTML);
        e.clipboardData.setData('text/plain', range.toString());
        e.preventDefault();
    }

    /**
     * 드래그로 잠긴(선택된) 블록은 없지만, 앵커(블록 전체를 표현하는 캐럿정지점)에 포커스가
     * 있는 상태의 복사를 처리한다. 이 앵커는 atomic 블록에서 여백 클릭 등으로 블록 전체가
     * 선택된 것처럼 보이는 상태(Backspace 로 블록 전체가 삭제되는 상태)를 나타내므로, 복사도
     * 블록 전체를 대상으로 한다.
     *
     * @param e - copy 이벤트
     */
    #copyFocusedAnchor(e: ClipboardEvent): void {
        if (e.clipboardData === null) {
            return;
        }

        const focusedElement = this.editor.focus.getElement();
        if (focusedElement?.type !== 'anchor') {
            return;
        }

        const block = focusedElement.block;
        const $container = document.createElement('div');
        $container.appendChild(block.$getBlock().getEl().cloneNode(true));

        e.clipboardData.setData('text/html', $container.innerHTML);
        e.clipboardData.setData('text/plain', block.$getBlock().getEl().textContent ?? '');
        e.preventDefault();
    }

    /**
     * 붙여넣기를 처리한다.
     *
     * @param e - paste 이벤트
     */
    async onPaste(e: ClipboardEvent): Promise<void> {
        e.preventDefault();

        const clipboardData = e.clipboardData;
        if (clipboardData === null) {
            return;
        }

        if (this.editor.focus.getBlock() === this.editor.root) {
            return;
        }

        let type: string;
        let data: any;
        if (clipboardData.files.length > 0) {
            type = 'Files';
            data = Array.from(clipboardData.files);
        } else if (clipboardData.getData('text/html') !== '') {
            type = 'text/html';
            data = clipboardData.getData('text/html');
            console.log('[origin]', data);
            data = Html.stripHtml(data, PASTE_TAGS, PASTE_ATTRS);
            data = CopyPaste.#demoteOrphanElements(data);

            data = Color.sanitizeStyles(this.editor, data);
            data = CopyPaste.#stripUselessMarks(data);
            data = `<div>${data}</div>`;
            data = CopyPaste.#stripEmpty(data);
        } else if (clipboardData.getData('text/plain') !== '') {
            type = 'text/plain';
            data = clipboardData.getData('text/plain');
        } else {
            return;
        }

        const deleted = this.#deleteSelection();
        const affectedContainers = new Set<Container>(deleted?.containers ?? []);

        console.log('[paste] ', type, data);

        const focusedElement = this.editor.focus.getElement();
        if (focusedElement !== null && data !== true) {
            data = await focusedElement.block.onPaste(type, data, focusedElement);
        }

        let beforeBlock = this.editor.focus.getBlock();
        let container = beforeBlock.getParent() ?? this.editor.root.body;

        while (data !== true) {
            const start = data;

            const orderedTypes = container.blocks.includes(beforeBlock.getType())
                ? [
                      beforeBlock.getType(),
                      ...container.blocks.filter((blockType) => blockType !== beforeBlock.getType()),
                  ]
                : container.blocks;

            for (const blockType of orderedTypes) {
                const BlockClass = container.block.getBlockClass(blockType);
                if (BlockClass === null) {
                    continue;
                }

                const target = BlockClass.create(this.editor);
                data = await target.onPaste(type, data, beforeBlock);

                if (target.$getBlock().getEl().isConnected === true) {
                    beforeBlock = target;
                }

                if (data === true) {
                    break;
                }
            }

            if (data === true) {
                break;
            }

            if (start === data) {
                if (container === this.editor.root.body) {
                    const dom = document.createElement('div');
                    dom.innerHTML = data as string;
                    const blocker = dom.firstElementChild?.firstElementChild ?? null;
                    if (blocker !== null) {
                        console.log('[paste] 아무도 처리하지 못해 통째로 버리는 태그', blocker.outerHTML);
                        blocker.remove();
                        data = dom.innerHTML;
                        continue;
                    }

                    console.log('[paste] 처리하지 못하고 남은 데이터', data);
                    break;
                }

                beforeBlock = container.block;
                container = beforeBlock.getParent() ?? this.editor.root.body;
            }
        }

        if (deleted !== null) {
            this.#restoreCaret(deleted.caretElement, deleted.caretBlock, deleted.caretOffset);
        }

        for (const affected of affectedContainers) {
            affected.onEdit();
        }
    }

    /**
     * 현재 선택영역을 온전히 지운다.
     *
     * @returns 삭제로 영향받은 컨테이너 목록과 캐럿이 남아야 할 지점
     */
    #deleteSelection(): {
        containers: Container[];
        caretElement: Element | null;
        caretBlock: Block | null;
        caretOffset: number | null;
    } | null {
        const range = this.editor.selection.getRange();
        if (range === null || range.collapsed === true) {
            return null;
        }

        const startElement = this.editor.focus.resolveElement(range.startContainer);
        const startBlock = startElement?.block ?? this.editor.focus.resolveBlock(range.startContainer);
        const endBlock =
            this.editor.focus.resolveElement(range.endContainer)?.block ??
            this.editor.focus.resolveBlock(range.endContainer);
        if (startBlock === null || endBlock === null) {
            return null;
        }

        const selectedBlocks = this.editor.selection.getSelectedBlocks();

        const containers = new Set<Container>();
        const startContainer = startBlock.getParent();
        const endContainer = endBlock.getParent();
        if (startContainer !== null) {
            containers.add(startContainer);
        }
        if (endContainer !== null) {
            containers.add(endContainer);
        }
        for (const block of selectedBlocks) {
            const parent = block.getParent();
            if (parent !== null) {
                containers.add(parent);
            }
        }

        // 잠긴(선택된) 블록 안쪽에 속한 컨테이너는 제외한다 — 그 블록 자체가 통째로 지워질
        // 텐데, 안쪽 컨테이너에 onEdit() 을 불러봐야 이미 고아가 된 컨테이너를 기록하는
        // 것밖에 안 되고, undo 도 엉뚱한 단위로 쪼개진다(실측 확인).
        const isInsideSelectedBlock = (container: Container): boolean => {
            let block: Block | null = container.block;
            while (block !== null) {
                if (selectedBlocks.includes(block)) {
                    return true;
                }
                block = block.getParent()?.block ?? null;
            }
            return false;
        };
        for (const container of [...containers]) {
            if (isInsideSelectedBlock(container) === true) {
                containers.delete(container);
            }
        }

        for (const container of containers) {
            container.onBeforeEdit();
        }

        const caretElement = startElement;
        const caretBlock = startBlock;
        const caretOffset =
            caretElement !== null
                ? this.editor.selection.getTextLength(caretElement, range.startContainer, range.startOffset)
                : null;

        range.deleteContents();

        this.editor.events.selectionChange.forceEnd();

        for (const block of selectedBlocks) {
            if (block === caretBlock) {
                if (block.getKind() === 'atomic') {
                    this.editor.events.convertAtomicRemnantToParagraph(block, caretElement);
                }
                continue;
            }

            block.$getBlock().getEl().remove();
        }

        this.#restoreCaret(caretElement, caretBlock, caretOffset);

        return { containers: [...containers], caretElement, caretBlock, caretOffset };
    }

    /**
     * 캐럿을 지정한 엘리먼트(정확한 오프셋이 있으면 그 지점) 또는 블록으로 되돌린다.
     *
     * @param caretElement - 캐럿을 되돌릴 엘리먼트 (있으면 우선)
     * @param caretBlock - 캐럿을 되돌릴 블록
     * @param caretOffset - `caretElement`가 있을 때 정확한 문자 오프셋까지 복원할 위치
     */
    #restoreCaret(caretElement: Element | null, caretBlock: Block | null, caretOffset: number | null): void {
        if (caretElement !== null) {
            (document.activeElement as HTMLElement | null)?.blur?.();
            caretElement.$getElement().getEl().focus();
            if (caretOffset !== null) {
                this.editor.selection.setOffsets(caretElement, caretOffset, caretOffset);
            }
            this.editor.focus.setElement(caretElement);
        } else if (caretBlock !== null) {
            caretBlock.focus(null);
        }
    }

    /**
     * HTML 문자열에서 내용이 비어있는 태그를 재귀적으로(자식부터) 제거한다.
     *
     * @param html - 원본 HTML 문자열
     * @param keep - 비어있어도 지우지 않을 태그명 목록(대문자, 기본값은 대표적인 void 요소들)
     * @returns 빈 태그가 제거된 HTML 문자열
     */
    static #stripEmpty(
        html: string,
        keep: string[] = [
            'IMG',
            'BR',
            'HR',
            'INPUT',
            'VIDEO',
            'AUDIO',
            'IFRAME',
            'EMBED',
            'SOURCE',
            'TRACK',
            'CANVAS',
            'AREA',
            'COL',
            'WBR',
        ]
    ): string {
        const $container = document.createElement('div');
        $container.innerHTML = html;

        const strip = (el: globalThis.Element): void => {
            for (const child of [...el.children]) {
                strip(child);
                if (keep.includes(child.tagName) === false && child.innerHTML.trim() === '') {
                    child.remove();
                }
            }
        };
        strip($container);

        return $container.innerHTML;
    }

    /**
     * `rootin-block`의 자식이 아닌 `rootin-element`를 DIV 로 변경한다.
     *
     * @param html - 원본 HTML 문자열
     * @returns 고아 `rootin-element`가 `div`로 강등된 HTML 문자열
     */
    static #demoteOrphanElements(html: string): string {
        const $container = document.createElement('div');
        $container.innerHTML = html;

        for (const $el of Array.from($container.querySelectorAll('rootin-element'))) {
            if ($el.closest('rootin-block') !== null) {
                continue;
            }

            const $div = document.createElement('div');
            while ($el.firstChild !== null) {
                $div.appendChild($el.firstChild);
            }
            $el.replaceWith($div);
        }

        return $container.innerHTML;
    }

    /**
     * 의미없는 마크업을 제거한다.
     *
     * @param html - 원본 HTML 문자열
     * @param tags - 이 판정을 적용할 태그명 목록(대소문자 무관, 기본값 `span`/`label`)
     * @returns 쓸모없어진 태그가 벗겨진 HTML 문자열
     */
    static #stripUselessMarks(html: string, tags: string[] = ['SPAN', 'LABEL']): string {
        const tagSet = new Set(tags.map((tag) => tag.toUpperCase()));

        const isBlank = (el: globalThis.Element): boolean =>
            (el.textContent ?? '').replace(/\u00A0/g, '').trim() === '';

        const strip = (parent: globalThis.Element): void => {
            for (const child of [...parent.children]) {
                strip(child);

                if (tagSet.has(child.tagName) === false) {
                    continue;
                }

                if (child.attributes.length > 0 && isBlank(child) === false) {
                    // 속성도 있고 내용도 있다 — 진짜 서식을 나르고 있으니 그대로 둔다.
                    continue;
                }

                while (child.firstChild !== null) {
                    parent.insertBefore(child.firstChild, child);
                }
                child.remove();
            }
        };

        const $container = document.createElement('div');
        $container.innerHTML = html;
        strip($container);

        return $container.innerHTML;
    }
}
